import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { ArrowLeftRight, RefreshCw, Calculator, TrendingUp, History } from "lucide-react";
import { motion } from "framer-motion";
import PageIntro from "../components/PageIntro";

const currencies = ["EUR", "AED", "GBP", "TRY", "USD"];

export default function FXEnginePage() {
  const [rates, setRates] = useState({});
  const [from, setFrom] = useState("AED");
  const [to, setTo] = useState("EUR");
  const [amount, setAmount] = useState("1000");
  const [converted, setConverted] = useState(null);
  const [updatedAt, setUpdatedAt] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchRates = useCallback(async () => {
    try {
      const { data } = await api.get("/fx/rates?base=EUR");
      setRates(data.rates || {});
      setUpdatedAt(data.updated_at || "");
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { fetchRates(); }, [fetchRates]);

  const handleConvert = async () => {
    if (!amount || isNaN(amount)) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/fx/convert?amount=${amount}&from_currency=${from}&to_currency=${to}`);
      setConverted(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const swapCurrencies = () => {
    setFrom(to);
    setTo(from);
    setConverted(null);
  };

  return (
    <div data-testid="fx-engine-page">
      <PageIntro
        pageKey="fx"
        title="Live FX Engine"
        subtitle="Real-time conversion between EUR, AED, GBP, TRY and USD — the currencies you actually hold."
        bullets={[
          { icon: Calculator, label: "Convert any amount", text: "Pick from / to and amount. Updated daily from ExchangeRate-API and snapshotted into our database." },
          { icon: TrendingUp, label: "90-day moving average", text: "Each rate is compared to its 90d average so you can spot favourable conversion windows." },
          { icon: History, label: "Live rates board", text: "All EUR pairs shown on the right; bookmark this page before any cross-border payment." },
        ]}
      />
      <div className="mb-8">
        <p className="text-[11px] tracking-[0.15em] uppercase text-white/60 font-sans mb-1">FX Engine</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-white tracking-tight">Currency Exchange</h1>
        <p className="text-white/60 text-sm mt-1">Live foreign exchange rates for your property portfolio</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Converter */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#0A0A0A] border border-white/[0.10] p-6"
          data-testid="fx-converter"
        >
          <p className="text-[11px] tracking-[0.15em] uppercase text-white/60 mb-6">Convert</p>

          <div className="space-y-4">
            <div>
              <label className="text-[10px] tracking-[0.15em] uppercase text-white/65 mb-1.5 block">Amount</label>
              <input
                data-testid="fx-amount-input"
                type="number"
                value={amount}
                onChange={e => { setAmount(e.target.value); setConverted(null); }}
                className="w-full bg-[#050505] border border-white/10 text-white px-4 py-3 text-lg font-mono placeholder:text-white/55 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-all outline-none"
              />
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="text-[10px] tracking-[0.15em] uppercase text-white/65 mb-1.5 block">From</label>
                <select
                  data-testid="fx-from-select"
                  value={from}
                  onChange={e => { setFrom(e.target.value); setConverted(null); }}
                  className="w-full bg-[#050505] border border-white/10 text-white px-4 py-3 font-mono focus:border-[#D4AF37] outline-none"
                >
                  {currencies.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <button
                onClick={swapCurrencies}
                data-testid="fx-swap-btn"
                className="mt-5 w-10 h-10 border border-white/10 flex items-center justify-center text-white/60 hover:text-[#D4AF37] hover:border-[#D4AF37]/30 transition-all"
              >
                <ArrowLeftRight className="w-4 h-4" />
              </button>

              <div className="flex-1">
                <label className="text-[10px] tracking-[0.15em] uppercase text-white/65 mb-1.5 block">To</label>
                <select
                  data-testid="fx-to-select"
                  value={to}
                  onChange={e => { setTo(e.target.value); setConverted(null); }}
                  className="w-full bg-[#050505] border border-white/10 text-white px-4 py-3 font-mono focus:border-[#D4AF37] outline-none"
                >
                  {currencies.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <button
              onClick={handleConvert}
              disabled={loading}
              data-testid="fx-convert-btn"
              className="w-full bg-[#D4AF37] text-black py-3 text-sm font-medium hover:bg-[#B5952F] transition-colors disabled:opacity-50"
            >
              {loading ? "Converting..." : "Convert"}
            </button>

            {converted && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#050505] border border-[#D4AF37]/20 p-5 mt-4"
                data-testid="fx-result"
              >
                <p className="text-white/60 text-xs mb-1">Result</p>
                <p className="font-mono text-2xl text-white">
                  {converted.converted?.toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-[#D4AF37]">{converted.to}</span>
                </p>
                <p className="text-white/65 text-xs mt-2 font-mono">
                  Rate: 1 {converted.from} = {converted.rate?.toFixed(6)} {converted.to}
                </p>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Rates Table */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#0A0A0A] border border-white/[0.10] p-6"
          data-testid="fx-rates-table"
        >
          <div className="flex items-center justify-between mb-6">
            <p className="text-[11px] tracking-[0.15em] uppercase text-white/60">Live Rates (Base: EUR)</p>
            <button onClick={fetchRates} className="text-white/65 hover:text-[#D4AF37] transition-colors" data-testid="fx-refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-0">
            {Object.entries(rates).map(([currency, rate]) => (
              <div key={currency} className="flex items-center justify-between py-4 border-b border-white/[0.08]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/[0.03] border border-white/[0.10] flex items-center justify-center">
                    <span className="font-mono text-xs text-[#D4AF37]">{currency}</span>
                  </div>
                  <div>
                    <p className="font-mono text-sm text-white">EUR/{currency}</p>
                    <p className="text-white/65 text-[10px]">
                      {currency === "AED" ? "UAE Dirham" :
                       currency === "GBP" ? "British Pound" :
                       currency === "EUR" ? "Euro" :
                       currency === "TRY" ? "Turkish Lira" :
                       currency === "USD" ? "US Dollar" : currency}
                    </p>
                  </div>
                </div>
                <span className="font-mono text-lg text-white">{rate.toFixed(4)}</span>
              </div>
            ))}
          </div>

          {updatedAt && (
            <p className="text-white/55 text-[10px] mt-4">
              Last updated: {new Date(updatedAt).toLocaleString()}
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
}
